import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Compass, Shield, Lightbulb, CheckCircle, Play, BookOpen, FileText, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { servicesData } from '../data/siteData';
import { useVideos, useBlogPosts, useBooks } from '../hooks/useContent';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
};

export default function Advisory() {
  const service = servicesData.advisory;
  
  // Fetch content from Contentful
  const { videos = [], isLoading: videosLoading } = useVideos();
  const { blogPosts = [], isLoading: postsLoading } = useBlogPosts();
  const { books = [], isLoading: booksLoading } = useBooks();

  // Filter content by "Advisory" tag
  const relatedVideos = Array.isArray(videos) ? videos.filter(v => 
    v.tags?.some(tag => tag.toLowerCase().includes('advisory')) ||
    v.title?.toLowerCase().includes('advisory') ||
    v.title?.toLowerCase().includes('strategy')
  ).slice(0, 3) : [];

  const relatedPosts = Array.isArray(blogPosts) ? blogPosts.filter(p => 
    p.tags?.some(tag => tag.toLowerCase().includes('advisory')) ||
    p.title?.toLowerCase().includes('advisory') ||
    p.title?.toLowerCase().includes('strategic')
  ).slice(0, 3) : [];

  const relatedBooks = Array.isArray(books) ? books.filter(b => 
    b.tags?.some(tag => tag.toLowerCase().includes('advisory')) ||
    b.title?.toLowerCase().includes('advisory') ||
    b.title?.toLowerCase().includes('strategy')
  ).slice(0, 2) : [];

  const isLoading = videosLoading || postsLoading || booksLoading;
  const hasRelatedContent = relatedVideos.length > 0 || relatedPosts.length > 0 || relatedBooks.length > 0;

  // Get offerings list (array of strings)
  const offeringsList = service.offerings || [];

  return (
    <div data-testid="advisory-page">
      {/* Hero Section */}
      <section className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 md:gap-20 items-center">
            {/* Content */}
            <motion.div
              initial="initial"
              animate="animate"
              variants={{ animate: { transition: { staggerChildren: 0.1 } } }}
            >
              <motion.p
                variants={fadeInUp}
                className="text-brand-secondary font-semibold tracking-widest uppercase text-sm mb-4"
              >
                {service.tagline}
              </motion.p>
              <motion.h1
                variants={fadeInUp}
                className="font-heading text-4xl md:text-6xl font-bold text-brand-primary leading-tight mb-6"
              >
                {service.title}
              </motion.h1>
              <motion.p variants={fadeInUp} className="text-slate-600 text-lg mb-8">
                {service.description}
              </motion.p>
              <motion.div variants={fadeInUp}>
                <Button
                  asChild
                  size="lg"
                  className="rounded-full bg-brand-secondary text-white hover:bg-brand-secondary/90 px-8 font-semibold shadow-lg shadow-orange-500/20"
                >
                  <Link to="/contact?service=advisory">
                    {service.cta} <ArrowRight className="ml-2 w-5 h-5" />
                  </Link>
                </Button>
              </motion.div>
            </motion.div>

            {/* Image */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-br from-brand-secondary/20 to-transparent rounded-3xl blur-2xl" />
                <img
                  src={service.image}
                  alt="Advisory"
                  className="relative w-full rounded-2xl shadow-xl"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Advisory Offerings */}
      <section className="py-20 md:py-32 bg-brand-accent">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-brand-primary mb-4">
              Advisory Services
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Long-term strategic partnership to help guide critical decisions and navigate complex challenges.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {offeringsList.map((offeringName, index) => (
              <motion.div
                key={offeringName}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-lg transition-shadow"
              >
                <div className="w-12 h-12 bg-brand-secondary/10 rounded-xl flex items-center justify-center mb-4">
                  {index % 3 === 0 && <Compass className="w-6 h-6 text-brand-secondary" />}
                  {index % 3 === 1 && <Shield className="w-6 h-6 text-brand-secondary" />}
                  {index % 3 === 2 && <Lightbulb className="w-6 h-6 text-brand-secondary" />}
                </div>
                <h3 className="font-heading text-xl font-bold text-brand-primary">
                  {offeringName}
                </h3>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Related Content Section - From Contentful */}
      <section className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-brand-primary mb-4">
              Related Content
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Explore videos, articles, and books tagged with "Advisory".
            </p>
          </motion.div>

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-brand-secondary" />
            </div>
          ) : hasRelatedContent ? (
            <>
              {relatedVideos.length > 0 && (
                <div className="mb-12">
                  <h3 className="font-heading text-xl font-bold text-brand-primary mb-6 flex items-center gap-2">
                    <Play className="w-5 h-5 text-brand-secondary" />
                    Videos & Podcasts
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {relatedVideos.map((video, index) => (
                      <motion.div key={video.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }}>
                        <Link to={`/videos/${video.slug}`} className="group block">
                          <div className="relative aspect-video rounded-xl overflow-hidden mb-3">
                            <img src={video.thumbnail} alt={video.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                            <div className="absolute inset-0 bg-black/30 flex items-center justify-center"><Play className="w-10 h-10 text-white" /></div>
                          </div>
                          <h4 className="font-semibold text-brand-primary group-hover:text-brand-secondary transition-colors line-clamp-2">{video.title}</h4>
                        </Link>
                      </motion.div>
                    ))}
                  </div>
                </div>
              )}
              {relatedPosts.length > 0 && (
                <div className="mb-12">
                  <h3 className="font-heading text-xl font-bold text-brand-primary mb-6 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-brand-secondary" />
                    Articles
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {relatedPosts.map((post, index) => (
                      <motion.div key={post.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }}>
                        <Link to={`/blog/${post.slug}`} className="group block">
                          <div className="aspect-[4/3] rounded-xl overflow-hidden mb-3">
                            <img src={post.image} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                          </div>
                          <h4 className="font-semibold text-brand-primary group-hover:text-brand-secondary transition-colors line-clamp-2">{post.title}</h4>
                        </Link>
                      </motion.div>
                    ))}
                  </div>
                </div>
              )}
              {relatedBooks.length > 0 && (
                <div>
                  <h3 className="font-heading text-xl font-bold text-brand-primary mb-6 flex items-center gap-2">
                    <BookOpen className="w-5 h-5 text-brand-secondary" />
                    Books
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {relatedBooks.map((book, index) => (
                      <motion.div key={book.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }}>
                        <Link to={`/books/${book.slug}`} className="group flex gap-4 p-4 bg-brand-accent rounded-xl hover:shadow-lg transition-shadow">
                          <img src={book.coverImage} alt={book.title} className="w-24 h-32 object-cover rounded-lg shadow" />
                          <div className="flex-1">
                            <h4 className="font-semibold text-brand-primary group-hover:text-brand-secondary transition-colors">{book.title}</h4>
                            <p className="text-sm text-slate-500 mt-2 line-clamp-2">{book.description}</p>
                          </div>
                        </Link>
                      </motion.div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12 bg-brand-accent rounded-2xl">
              <p className="text-slate-600 mb-2">No related content yet.</p>
              <p className="text-sm text-slate-500">Tag your content with "Advisory" in Contentful to show it here.</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
